import {
  AutoFixHigh,
  Comment,
  ContentCopy,
  Create,
  Description,
  Forum,
  Speed,
  UnfoldLess,
  UnfoldMore
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

interface AIPresetActionsListProps {
  hasFeature: (feature: string) => boolean;
  isTokenDepleted: boolean;
  isProcessing: boolean;
  actionProcessing: string | null;
  onActionClick: (action: string) => void;
  onAddComment?: () => void;
  onCopyClick: () => void;
  isSelectionEmpty: boolean;
}

export const AIPresetActionsList = memo(({
  hasFeature,
  isTokenDepleted,
  isProcessing,
  actionProcessing,
  onActionClick,
  onAddComment,
  onCopyClick,
  isSelectionEmpty,
}: AIPresetActionsListProps) => {
  const { t } = useTranslation();

  const renderActionItem = (
    action: string,
    label: string,
    IconComponent: any,
    iconColor?: string
  ) => {
    const isThisProcessing = isProcessing && actionProcessing === action;

    return (
      <ListItem disablePadding key={action}>
        <Tooltip
          title={isTokenDepleted ? t('entity_image_manager.common.tokens_depleted_tooltip') : ''}
          placement="left"
        >
          <div style={{ width: '100%' }}>
            <ListItemButton
              onClick={() => onActionClick(action)}
              disabled={isTokenDepleted || isProcessing}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {isThisProcessing ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconComponent fontSize="small" sx={iconColor ? { color: iconColor } : undefined} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={label}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItemButton>
          </div>
        </Tooltip>
      </ListItem>
    );
  };

  return (
    <>
      {/* Action List Section 1 */}
      {onAddComment && (
        <List dense sx={{ py: 0.5 }}>
          <ListItem disablePadding>
            <ListItemButton onClick={onAddComment}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Comment fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('editor.comment')} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItemButton>
          </ListItem>
        </List>
      )}

      {hasFeature('ai_writer_allowed') && (
        <>
          <Divider />
          {/* AI Assistance Section */}
          <List dense sx={{ py: 0.5 }}>
            {!isSelectionEmpty && (
              <>
                {renderActionItem('rewrite_selection', t('editor.rewrite_selection'), AutoFixHigh)}
                {renderActionItem('expand', t('editor.expand'), UnfoldMore)}
                {renderActionItem('shorten', t('editor.shorten'), UnfoldLess)}
                {renderActionItem('improve_dialogue', t('editor.improve_dialogue'), Forum)}
                {renderActionItem('improve_description', t('editor.improve_description'), Description)}
                {renderActionItem('fix_pacing', t('editor.fix_pacing'), Speed)}
              </>
            )}
            {renderActionItem('continue_writing', t('editor.continue_writing'), Create, 'primary.main')}
          </List>
        </>
      )}

      {!isSelectionEmpty && (
        <>
          <Divider />

          {/* Utility Section */}
          <List dense sx={{ py: 0.5 }}>
            <ListItem disablePadding>
              <ListItemButton onClick={onCopyClick}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ContentCopy fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('editor.copy_selection')} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}
    </>
  );
});

AIPresetActionsList.displayName = 'AIPresetActionsList';
